import * as Yup from 'yup';

export const buildSuperProfileFormInitialValues = (superProfile) => {
  const initialValues = {
    internalName: superProfile ? superProfile.internalName : '',
    publicTitle: superProfile ? superProfile.publicTitle : '',
    publicDescription: superProfile ? superProfile.publicDescription : '',
    jobProfileId: superProfile ? superProfile.jobProfile.id : null,
    occupationalLevel: superProfile ? superProfile.occupationalLevel : '',
    workCategory: superProfile ? superProfile.workCategory : '',
    workArea: superProfile ? superProfile.workArea : '',
    workplaceDetailsType: superProfile ? superProfile.workplaceDetails.type : '',
    workplaceDetailsOfficeOnlyCountry: getWorkplaceDetailsOfficeOnlyCountryInitialValue(superProfile),
    workplaceDetailsOfficeOnlyRegions: getWorkplaceDetailsOfficeOnlyRegionsInitialValue(superProfile),
    workplaceDetailsOfficeAndRemoteCountry: getWorkplaceDetailsOfficeAndRemoteCountryInitialValue(superProfile),
    workplaceDetailsOfficeAndRemoteRegions: getWorkplaceDetailsOfficeAndRemoteRegionsInitialValue(superProfile),
    workplaceDetailsRemoteOnlyFromAnyCountry: getWorkplaceDetailsRemoteOnlyFromAnyCountryInitialValue(superProfile),
    workplaceDetailsRemoteOnlyCountries: getWorkplaceDetailsRemoteOnlyCountriesInitialValue(superProfile),
    requiredMinimumEducation: superProfile ? superProfile.requiredMinimumEducation : '',
    educationRelevance: superProfile ? superProfile.educationRelevance : '',
    requiredJobSeniority: superProfile ? superProfile.requiredJobSeniority : '',
    jobSeniorityRelevance: superProfile ? superProfile.jobSeniorityRelevance : '',
    requiredManagementExperience: superProfile ? superProfile.requiredManagementExperience : '',
    requiredLanguageProficiencies: superProfile
      ? superProfile.requiredLanguageProficiencies
      : [
          {
            language: '',
            proficiency: '',
          },
        ],
    minimumOverallAssessmentScoreRequired: superProfile ? superProfile.minimumOverallAssessmentScoreRequired : 75,
    minimumOverallQAndRScoreRequired: superProfile ? superProfile.minimumOverallQAndRScoreRequired : 67,
  };

  if (!superProfile) {
    initialValues.jobProfileId = '';
  }

  return initialValues;
};

export const buildSuperProfileFormValidationSchema = (superProfile) => {
  return Yup.object().shape(
    {
      internalName: Yup.string().required(),
      publicTitle: Yup.string().required(),
      publicDescription: Yup.string().required(),
      jobProfileId: Yup.number().when([], {
        is: () => !superProfile,
        then: Yup.number().required(),
        otherwise: Yup.number().notRequired(),
      }),
      occupationalLevel: Yup.string().required(),
      workCategory: Yup.string().required(),
      workArea: Yup.string().required(),
      workplaceDetailsType: Yup.string().required(),
      workplaceDetailsOfficeOnlyCountry: Yup.string().when('workplaceDetailsType', {
        is: (val) => !!(val && val.length > 0) && val === 'OFFICE_ONLY',
        then: Yup.string().required(),
        otherwise: Yup.string().notRequired(),
      }),
      workplaceDetailsOfficeOnlyRegions: Yup.array().when('workplaceDetailsType', {
        is: (val) => !!(val && val.length > 0) && val === 'OFFICE_ONLY',
        then: Yup.array()
          .min(1)
          .required(),
        otherwise: Yup.array().notRequired(),
      }),
      workplaceDetailsOfficeAndRemoteCountry: Yup.string().when('workplaceDetailsType', {
        is: (val) => !!(val && val.length > 0) && val === 'OFFICE_AND_REMOTE',
        then: Yup.string().required(),
        otherwise: Yup.string().notRequired(),
      }),
      workplaceDetailsOfficeAndRemoteRegions: Yup.array().when('workplaceDetailsType', {
        is: (val) => !!(val && val.length > 0) && val === 'OFFICE_AND_REMOTE',
        then: Yup.array()
          .min(1)
          .required(),
        otherwise: Yup.array().notRequired(),
      }),
      workplaceDetailsRemoteOnlyFromAnyCountry: Yup.boolean().when(
        ['workplaceDetailsType', 'workplaceDetailsRemoteOnlyCountries'],
        {
          is: (workplaceDetailsType, workplaceDetailsRemoteOnlyCountries) =>
            !!(workplaceDetailsType && workplaceDetailsType.length > 0) &&
            workplaceDetailsType === 'REMOTE_ONLY' &&
            (!!workplaceDetailsRemoteOnlyCountries && workplaceDetailsRemoteOnlyCountries.length === 0),
          then: Yup.boolean()
            .required()
            .oneOf([true]),
          otherwise: Yup.boolean().notRequired(),
        },
      ),
      workplaceDetailsRemoteOnlyCountries: Yup.array().when(
        ['workplaceDetailsType', 'workplaceDetailsRemoteOnlyFromAnyCountry'],
        {
          is: (workplaceDetailsType, workplaceDetailsRemoteOnlyFromAnyCountry) =>
            !!(workplaceDetailsType && workplaceDetailsType.length > 0) &&
            workplaceDetailsType === 'REMOTE_ONLY' &&
            !workplaceDetailsRemoteOnlyFromAnyCountry,
          then: Yup.array()
            .min(1)
            .required(),
          otherwise: Yup.array().notRequired(),
        },
      ),
      requiredMinimumEducation: Yup.string().required(),
      educationRelevance: Yup.string().required(),
      requiredJobSeniority: Yup.string().required(),
      jobSeniorityRelevance: Yup.string().required(),
      requiredManagementExperience: Yup.string().required(),
      requiredLanguageProficiencies: Yup.array()
        .of(
          Yup.object().shape({
            language: Yup.string().required(),
            proficiency: Yup.string().required(),
          }),
        )
        .min(1)
        .required(),
      minimumOverallAssessmentScoreRequired: Yup.number()
        .min(1)
        .max(100)
        .required(),
      minimumOverallQAndRScoreRequired: Yup.number()
        .min(1)
        .max(100)
        .required(),
    },
    ['workplaceDetailsRemoteOnlyFromAnyCountry', 'workplaceDetailsRemoteOnlyCountries'],
  );
};

export const buildWorkAreaOptions = (occupationalLevel, workCategory) => {
  if (
    occupationalLevel === 'INTERN_STUDENT' ||
    occupationalLevel === 'FREELANCER' ||
    occupationalLevel === 'TEMPORARY_EMPLOYEE' ||
    occupationalLevel === 'EMPLOYEE'
  ) {
    if (workCategory === 'INFORMATION_TECHNOLOGY') {
      return [
        { label: 'Database', value: 'DATABASE' },
        { label: 'Development and programming', value: 'DEVELOPMENT_AND_PROGRAMMING' },
        { label: 'ERP and corporate systems', value: 'ERP_AND_CORPORATE_SYSTEMS' },
        { label: 'Internet and www', value: 'INTERNET_AND_WWW' },
        { label: 'Maintenance and support', value: 'MAINTENANCE_AND_SUPPORT' },
        { label: 'Tele- and data communication', value: 'TELE_AND_DATA_COMMUNICATION' },
        { label: 'Other', value: 'OTHER' },
      ];
    }

    if (workCategory === 'TECHNICAL_AND_MECHANICAL') {
      return [
        { label: 'Building and construction engineering', value: 'BUILDING_AND_CONSTRUCTION_ENGINEERING' },
        { label: 'Chemistry and biotechnology', value: 'CHEMISTRY_AND_BIOTECHNOLOGY' },
        { label: 'Electrical engineering', value: 'ELECTRICAL_ENGINEERING' },
        { label: 'Foods', value: 'FOODS' },
        { label: 'Mechanical engineering', value: 'MECHANICAL_ENGINEERING' },
        { label: 'Medicine', value: 'MEDICINE' },
        { label: 'Production and process engineering', value: 'PRODUCTION_AND_PROCESS_ENGINEERING' },
        { label: 'Apprentice', value: 'APPRENTICE' },
        { label: 'Other', value: 'OTHER' },
      ];
    }

    if (workCategory === 'TRADE_AND_SERVICE') {
      return [
        { label: 'Cleaning', value: 'CLEANING' },
        { label: 'Delivery services', value: 'DELIVERY_SERVICES' },
        { label: 'Energy and utilities', value: 'ENERGY_AND_UTILITIES' },
        { label: 'Hairdresser and personal care', value: 'HAIRDRESSER_AND_PERSONAL_CARE' },
        { label: 'Hospitality', value: 'HOSPITALITY' },
        { label: 'Hotel, restaurant, kitchen', value: 'HOTEL_RESTAURANT_KITCHEN' },
        { label: 'Property service', value: 'PROPERTY_SERVICE' },
        { label: 'Real estate service', value: 'REAL_ESTATE_SERVICE' },
        { label: 'Retail/consumer', value: 'RETAIL_CONSUMER' },
        { label: 'Security', value: 'SECURITY' },
        { label: 'Tourism', value: 'TOURISM' },
        { label: 'Apprentice', value: 'APPRENTICE' },
        { label: 'Other', value: 'OTHER' },
      ];
    }

    if (workCategory === 'INDUSTRY_AND_CRAFT') {
      return [
        { label: 'Agriculture, forestry and fishing', value: 'AGRICULTURE_FORESTRY_AND_FISHING' },
        { label: 'Building and carpentry', value: 'BUILDING_AND_CARPENTRY' },
        { label: 'Construction', value: 'CONSTRUCTION' },
        { label: 'Electricity', value: 'ELECTRICITY' },
        { label: 'Industrial production', value: 'INDUSTRIAL_PRODUCTION' },
        { label: 'Iron and metal', value: 'IRON_AND_METAL' },
        { label: 'Mechanics and auto', value: 'MECHANICS_AND_AUTO' },
        { label: 'Nutrients and stimulants', value: 'NUTRIENTS_AND_STIMULANTS' },
        { label: 'Painting and surface treatment', value: 'PAINTING_AND_SURFACE_TREATMENT' },
        { label: 'Plumbing', value: 'PLUMBING' },
        { label: 'Stock/warehouse', value: 'STOCK_WAREHOUSE' },
        { label: 'Textiles and handcrafts', value: 'TEXTILES_AND_HANDCRAFTS' },
        { label: 'Transport and logistics', value: 'TRANSPORT_AND_LOGISTICS' },
        { label: 'Wood and furniture', value: 'WOOD_AND_FURNITURE' },
        { label: 'Manufacturing', value: 'MANUFACTURING' },
        { label: 'Apprentice', value: 'APPRENTICE' },
        { label: 'Other', value: 'OTHER' },
      ];
    }

    if (workCategory === 'HEALTHCARE_MEDICAL_AND_SOCIAL') {
      return [
        { label: 'Childcare', value: 'CHILDCARE' },
        { label: 'Dentist clinic staff', value: 'DENTIST_CLINIC_STAFF' },
        { label: 'Dentist', value: 'DENTIST' },
        { label: 'Healthcare work', value: 'HEALTHCARE_WORK' },
        { label: 'Medical doctor', value: 'MEDICAL_DOCTOR' },
        { label: 'Medical secretary', value: 'MEDICAL_SECRETARY' },
        { label: 'Midwife', value: 'MIDWIFE' },
        { label: 'Nurse', value: 'NURSE' },
        { label: 'Pedagogue', value: 'PEDAGOGUE' },
        { label: 'Psychiatry', value: 'PSYCHIATRY' },
        { label: 'Psychology', value: 'PSYCHOLOGY' },
        { label: 'Social counseling', value: 'SOCIAL_COUNSELING' },
        { label: 'Social services', value: 'SOCIAL_SERVICES' },
        { label: 'Technical healthcare work', value: 'TECHNICAL_HEALTHCARE_WORK' },
        { label: 'Therapy and rehabilitation', value: 'THERAPY_AND_REHABILITATION' },
        { label: 'Treatment and care', value: 'TREATMENT_AND_CARE' },
        { label: 'Other', value: 'OTHER' },
      ];
    }

    if (workCategory === 'SALES_MARKETING_AND_COMMUNICATION') {
      return [
        { label: 'Communication', value: 'COMMUNICATION' },
        { label: 'Design', value: 'DESIGN' },
        { label: 'Graphical', value: 'GRAPHICAL' },
        { label: 'Journalism', value: 'JOURNALISM' },
        { label: 'Marketing', value: 'MARKETING' },
        { label: 'Media and entertainment', value: 'MEDIA_AND_ENTERTAINMENT' },
        { label: 'Real estate agent', value: 'REAL_ESTATE_AGENT' },
        { label: 'Sales', value: 'SALES' },
        { label: 'Telemarketing', value: 'TELEMARKETING' },
        { label: 'Apprentice', value: 'APPRENTICE' },
        { label: 'Other', value: 'OTHER' },
      ];
    }

    if (workCategory === 'EDUCATION_AND_RESEARCH') {
      return [
        { label: 'Research', value: 'RESEARCH' },
        { label: 'Teaching, higher educations', value: 'TEACHING_HIGHER_EDUCATIONS' },
        { label: 'Teaching, lower educations', value: 'TEACHING_LOWER_EDUCATIONS' },
        { label: 'Apprentice', value: 'APPRENTICE' },
        { label: 'Other', value: 'OTHER' },
      ];
    }

    if (workCategory === 'OFFICE_AND_ECONOMY') {
      return [
        { label: 'Academic work', value: 'ACADEMIC_WORK' },
        { label: 'Accounting', value: 'ACCOUNTING' },
        { label: 'Administration', value: 'ADMINISTRATION' },
        { label: 'Banking', value: 'BANKING' },
        { label: 'Economy', value: 'ECONOMY' },
        { label: 'Finance', value: 'FINANCE' },
        { label: 'Insurance', value: 'INSURANCE' },
        { label: 'Law', value: 'LAW' },
        { label: 'Logistics and shipping', value: 'LOGISTICS_AND_SHIPPING' },
        { label: 'Office work', value: 'OFFICE_WORK' },
        { label: 'Political work', value: 'POLITICAL_WORK' },
        { label: 'Purchasing/procurement', value: 'PURCHASING_PROCUREMENT' },
        { label: 'Reception', value: 'RECEPTION' },
        { label: 'Secretary', value: 'SECRETARY' },
        { label: 'Translation and language', value: 'TRANSLATION_AND_LANGUAGE' },
        { label: 'Apprentice', value: 'APPRENTICE' },
        { label: 'Other', value: 'OTHER' },
      ];
    }

    if (workCategory === 'OTHER') {
      return [
        { label: 'Non-profit', value: 'NON_PROFIT' },
        { label: 'Culture and Church', value: 'CULTURE_AND_CHURCH' },
        { label: 'Military and Police', value: 'MILITARY_AND_POLICE' },
        { label: 'Other', value: 'OTHER' },
      ];
    }
  }

  if (occupationalLevel === 'MANAGER') {
    return [
      { label: 'Department manager', value: 'DEPARTMENT_MANAGER' },
      { label: 'HR/personnel manager', value: 'HR_PERSONNEL_MANAGER' },
      { label: 'Marketing manager', value: 'MARKETING_MANAGER' },
      { label: 'Project manager', value: 'PROJECT_MANAGER' },
      { label: 'Retail manager', value: 'RETAIL_MANAGER' },
      { label: 'Sales manager', value: 'SALES_MANAGER' },
      { label: 'Team leader', value: 'TEAM_LEADER' },
      { label: 'Other', value: 'OTHER' },
    ];
  }

  if (occupationalLevel === 'LEADER') {
    return [
      { label: 'Chief Compliance Officer', value: 'CHIEF_COMPLIANCE_OFFICER' },
      { label: 'Chief Executive Officer', value: 'CHIEF_EXECUTIVE_OFFICER' },
      { label: 'Chief Finance Officer', value: 'CHIEF_FINANCE_OFFICER' },
      { label: 'Chief Human Resources Officer', value: 'CHIEF_HUMAN_RESOURCES_OFFICER' },
      { label: 'Chief Information Officer', value: 'CHIEF_INFORMATION_OFFICER' },
      { label: 'Chief Marketing Officer', value: 'CHIEF_MARKETING_OFFICER' },
      { label: 'Chief Operations Officer', value: 'CHIEF_OPERATIONS_OFFICER' },
      { label: 'Chief Sales Officer', value: 'CHIEF_SALES_OFFICER' },
      { label: 'Chief Security Officer', value: 'CHIEF_SECURITY_OFFICER' },
      { label: 'Chief Strategy Officer', value: 'CHIEF_STRATEGY_OFFICER' },
      { label: 'Chief Success Officer', value: 'CHIEF_SUCCESS_OFFICER' },
      { label: 'Chief Technology Officer', value: 'CHIEF_TECHNOLOGY_OFFICER' },
      { label: 'Other C-level executive', value: 'OTHER_C_LEVEL_EXECUTIVE' },
      { label: 'Public sector executive', value: 'PUBLIC_SECTOR_EXECUTIVE' },
      { label: 'Director', value: 'DIRECTOR' },
      { label: 'Other', value: 'OTHER' },
    ];
  }

  if (occupationalLevel === 'OTHER') {
    return [{ label: 'Board', value: 'BOARD' }, { label: 'Other', value: 'OTHER' }];
  }

  return [];
};

const getWorkplaceDetailsOfficeOnlyCountryInitialValue = (superProfile) => {
  if (!superProfile) {
    return '';
  }

  return superProfile.workplaceDetails.officeOnlyWorkplaceDetailsData
    ? superProfile.workplaceDetails.officeOnlyWorkplaceDetailsData.country
    : '';
};

const getWorkplaceDetailsOfficeOnlyRegionsInitialValue = (superProfile) => {
  if (!superProfile) {
    return [];
  }

  return superProfile.workplaceDetails.officeOnlyWorkplaceDetailsData
    ? superProfile.workplaceDetails.officeOnlyWorkplaceDetailsData.regions
    : [];
};

const getWorkplaceDetailsOfficeAndRemoteCountryInitialValue = (superProfile) => {
  if (!superProfile) {
    return '';
  }

  return superProfile.workplaceDetails.officeAndRemoteWorkplaceDetailsData
    ? superProfile.workplaceDetails.officeAndRemoteWorkplaceDetailsData.country
    : '';
};

const getWorkplaceDetailsOfficeAndRemoteRegionsInitialValue = (superProfile) => {
  if (!superProfile) {
    return [];
  }

  return superProfile.workplaceDetails.officeAndRemoteWorkplaceDetailsData
    ? superProfile.workplaceDetails.officeAndRemoteWorkplaceDetailsData.regions
    : [];
};

const getWorkplaceDetailsRemoteOnlyFromAnyCountryInitialValue = (superProfile) => {
  if (!superProfile) {
    return false;
  }

  return superProfile.workplaceDetails.remoteOnlyWorkplaceDetailsData
    ? superProfile.workplaceDetails.remoteOnlyWorkplaceDetailsData.fromAnyCountry
    : false;
};

const getWorkplaceDetailsRemoteOnlyCountriesInitialValue = (superProfile) => {
  if (!superProfile) {
    return [];
  }

  return superProfile.workplaceDetails.remoteOnlyWorkplaceDetailsData
    ? superProfile.workplaceDetails.remoteOnlyWorkplaceDetailsData.countries
    : [];
};
